import { Checkbox, FormField } from 'semantic-ui-react';

/**
 * The consents a person gives for themselves.
 *
 * ---------------------------------------------------------------------------------------
 * THE WORDING IS THE PAPER FORM'S, AND THAT IS THE POINT
 * ---------------------------------------------------------------------------------------
 *
 * Until 2026-10-13 this asked seven questions, three of which split data use into aggregate /
 * reporting / programs. The paper notice and consent form does not split it, so a Centre
 * Manager was being asked to answer questions the participant was never asked — a
 * consent-quality problem rather than a layout one.
 *
 * It now asks the five the paper form asks, in its words, and **defers to the notice and
 * consent form for the detail** rather than paraphrasing it badly. Katja and Ants settled this
 * against the DPDPA.
 *
 * ---------------------------------------------------------------------------------------
 * EVERY ONE IS OPTIONAL
 * ---------------------------------------------------------------------------------------
 *
 * A blank tick is a real answer — "not given" — and the follow-up worklist
 * (`/people` → Advanced filters → "Missing H&S / data consent") exists to find them. Making any
 * of these required would hide the very people it is meant to surface.
 */

export interface ConsentState {
    picturePermission: boolean;
    consentDataCollection: boolean;
    healthSafetyBriefed: boolean;
    consentHealthSafety: boolean;
    idSightedByCM: boolean;
    /**
     * Retired from the form on 2026-10-13 and derived server-side from
     * `consentDataCollection`. Kept on the state so existing rows round-trip unchanged through
     * the profile editor; never rendered.
     */
    consentDataAggregate: boolean;
    consentDataReporting: boolean;
    consentDataPrograms: boolean;
}

export const EMPTY_CONSENT: ConsentState = {
    picturePermission: false,
    consentDataCollection: false,
    healthSafetyBriefed: false,
    consentHealthSafety: false,
    idSightedByCM: false,
    consentDataAggregate: false,
    consentDataReporting: false,
    consentDataPrograms: false,
};

interface ConsentToggle {
    key: keyof ConsentState;
    label: string;
    hint?: string;
}

/**
 * The three headings from the paper form, each with the questions that belong under it.
 *
 * Grouped rather than listed flat because, in Katja's words, *"all listed together like that,
 * stuff gets missed"* — and because the groups are the form's own structure, so a Centre
 * Manager reading one next to the other sees the same shape in both.
 *
 * Labels live here so the profile editor and this form cannot word them differently.
 */
export const CONSENT_GROUPS: Array<{ title: string; toggles: ConsentToggle[] }> = [
    {
        title: 'Personal Information',
        toggles: [
            {
                key: 'consentDataCollection',
                label: 'Consents to the collection and use of their information in reporting, '
                    + 'social media, running programs, as set out in detail on the notice and '
                    + 'consent form.',
            },
            {
                key: 'picturePermission',
                label: 'Picture permission',
                hint: 'This is yes if the above toggle is yes, unless the participant '
                    + 'specifically indicated otherwise.',
            },
        ],
    },
    {
        title: 'Health and Safety',
        toggles: [
            { key: 'healthSafetyBriefed', label: 'Briefed on health and safety' },
            {
                key: 'consentHealthSafety',
                label: 'Consents to the health and safety risks of their program and agrees to '
                    + 'comply with safety instructions.',
            },
        ],
    },
    {
        title: 'ID',
        toggles: [
            { key: 'idSightedByCM', label: 'Their ID has been sighted by the Centre Manager.' },
        ],
    },
];

/** Flattened, for anything that wants the labels without the grouping. */
export const CONSENT_LABELS: ConsentToggle[] = CONSENT_GROUPS.flatMap((g) => g.toggles);

export const ConsentSection = ({
    value,
    onChange,
    /**
     * Whether picture permission has been answered in its own right yet.
     *
     * Held by the parent rather than here so it survives this component re-mounting, and
     * deliberately **not** a stored column: it describes how the form was filled in, not a
     * fact about the person.
     */
    picturePermissionTouched,
    onPicturePermissionTouched,
}: {
    value: ConsentState;
    onChange: (next: ConsentState) => void;
    picturePermissionTouched: boolean;
    onPicturePermissionTouched: () => void;
}) => {
    /**
     * Picture permission follows the general consent until somebody answers it separately.
     *
     * The paper form does not ask it as its own question — it is part of the same consent —
     * so the default has to match. But a participant who says "yes to everything except
     * photographs" must be recordable, and once that has been said the toggle stops following
     * or the next change to the consent above would silently overwrite it.
     */
    const setConsentDataCollection = (next: boolean) => {
        onChange({
            ...value,
            consentDataCollection: next,
            ...(picturePermissionTouched ? {} : { picturePermission: next }),
        });
    };

    const toggle = (key: keyof ConsentState) => {
        if (key === 'consentDataCollection') {
            setConsentDataCollection(!value.consentDataCollection);
            return;
        }

        if (key === 'picturePermission') {
            onPicturePermissionTouched();
        }

        onChange({ ...value, [key]: !value[key] });
    };

    return (
        <div className="creator-section">
            <div className="creator-section-title">Consent</div>
            <p className="creator-section-hint">
                The detail is on the participant's notice and consent form — these record what
                they answered. Leave anything not yet given unticked; people with gaps show up
                in the "Missing H&amp;S / data consent" filter on the People page.
            </p>

            <FormField>
                <div className="consent-groups">
                    {CONSENT_GROUPS.map(({ title, toggles }) => (
                        <div className="consent-group" key={title}>
                            <div className="consent-group-title">{title}</div>
                            {toggles.map(({ key, label, hint }) => (
                                <div key={key} className="consent-row">
                                    <Checkbox
                                        toggle
                                        // Semantic only emits `id`/`htmlFor` when given an id.
                                        // Without one the input has no accessible name at all —
                                        // invisible to a screen reader, and the label is
                                        // clickable only through Semantic's own JS.
                                        id={`consent-${key}`}
                                        label={label}
                                        checked={value[key]}
                                        onChange={() => toggle(key)}
                                    />
                                    {hint && <div className="consent-hint">{hint}</div>}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </FormField>
        </div>
    );
};
